import { Option, OptionsField } from "@imi-ernd-be/hyselect-engine";
import { TFunction } from "i18next";
import { assert, get, isArray, isObject } from "radashi";
import { toArray } from "../../vector";

export type OptionDefinition<Extension = unknown> = {
  default: boolean;
  disabled: boolean;
  id: string;
  label: string | null;
  translationKey: string | null;
} & Extension;

export type OptionsFieldDefinition<OptionExtension = unknown> = {
  default: string;
  disabled: boolean;
  id: string;
  label: string | null;
  options: OptionDefinition<OptionExtension>[];
  translationKey: string | null;
  type: "options";
};

export function formatOptionLabel(
  option: { label: string | null; translationKey: string | null },
  t: TFunction,
): string {
  if (option.translationKey) {
    return t(option.translationKey);
  }
  return option.label || "";
}

export function getOptionsFieldDefinition(
  group: OptionsField | null,
): OptionsFieldDefinition {
  assert(group, "OptionsField is null in getOptionsFieldDefinition");
  const options = toArray(group.getOptions()).map((option) =>
    buildOption(option),
  );

  return {
    default: group.getDefaultOption()?.id ?? options[0]?.id ?? "",
    disabled: group.disabled,
    id: group.id,
    label: group.hasTranslationKey ? null : group.labelOrTranslationKey,
    options,
    translationKey: group.hasTranslationKey
      ? group.labelOrTranslationKey
      : null,
    type: "options",
  };
}

export function isOptionsDefinition(
  definition: unknown,
): definition is OptionsFieldDefinition {
  return (
    isObject(definition) &&
    get(definition, "type") === "options" &&
    isArray(get(definition, "options"))
  );
}

function buildOption(option: Option): OptionDefinition {
  return {
    default: false,
    disabled: option.disabled,
    id: option.id,
    label: option.hasTranslationKey ? null : option.labelOrTranslationKey,
    translationKey: option.hasTranslationKey
      ? option.labelOrTranslationKey
      : null,
  };
}
